import React, { useState, useEffect, useCallback, useContext } from 'react';
import styles from './FlatsPage.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Card from '../../components/CardsComponents/FlatCard/FlatCard';
import Filter from '../../components/FiltersComponents/SmallFiltersComponents/Filter/Filter';
import FlatFilters from '../../components/FiltersComponents/BigFiltersComponents/FlatFilters/FlatFilters';
import { FilterSmallState } from '../../components/FiltersComponents/SmallFiltersComponents/Filter/Filter';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import { OptionData } from '../../components/CardsComponents/types';
import Features, { FeaturesData } from '../../components/Features/Features';
import { Helmet } from 'react-helmet';
import ActiveFilterButton from '../../components/UI/ActiveFilterButton/ActiveFilterButton';
import SelectButton from '../../components/UI/SelectButton/SelectButton';
import NotFoundBlock from '../../components/NotFoundBlock/NotFoundBlock';
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import { calculateDiscountedPrice } from '../../utils/discountPriceUtils';
import CardsLoader from '../../assets/images/CardsLoader.gif';
import api from '../../utils/api';

interface FlatFilterBigState {
  type: string[];
  price_range: [number, number];
  floor_range: [number, number];
  area_range?: [number, number];
  window_options: string[];
  special_options: string[];
}

const INITIAL_LOAD_COUNT = 12;
const LOAD_MORE_COUNT = 12;

export default function FlatsPage() {
  const [cardData, setCardData] = useState<any[]>([]);
  const [initialAreaRange, setInitialAreaRange] = useState<[number, number]>([0, 0]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [displayedCards, setDisplayedCards] = useState<any[]>([]);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [featuresData, setFeaturesData] = useState<FeaturesData[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);

  const [smallFilters, setSmallFilters] = useState<FilterSmallState>({
    type: [],
    price_range: [0, 0],
    floor_range: [0, 0],
  });

  const [filters, setFilters] = useState<FlatFilterBigState>({
    type: [],
    window_options: [],
    special_options: [],
    price_range: [0, 0],
    floor_range: [0, 0],
    area_range: initialAreaRange,
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 720);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitialLoad(true);
        const [flatsResponse, featuresResponse] = await Promise.all([
          api.flats.getAll(),
          api.flats.getFeatures(),
        ]);
        
        const featuresData = Array.isArray(featuresResponse) ? featuresResponse
          .map((feature: any) => ({
            id: feature.id,
            text: feature.text,
            image: feature.image,
          }))
          .filter((feature, index, self) => 
            index === self.findIndex(f => f.text === feature.text)
          ) : [];

        const allFlats = Array.isArray(flatsResponse) ? flatsResponse : [];
        const availableFlats = allFlats.filter((flat: any) => flat.available === true);
        availableFlats.sort((a: any, b: any) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));

        const prices = availableFlats
          .map((flat: any) => {
            if (flat.price == null) return null;
            const price = typeof flat.price === 'string' ? parseFloat(flat.price.replace(/ /g, '')) : flat.price;
            const discount = (flat.discountProperty ?? flat.discount_property ?? 0) / 100;
            return price * (1 - discount);
          })
          .filter((price: number | null): price is number => price !== null);

        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
        const formattedPrice = minPrice.toLocaleString('ru-RU');

        if (minPrice > 0) {
          featuresData.push({
            id: featuresData.length + 1,
            text: `От ${formattedPrice} ₽`,
            image: undefined,
          });
        }
        
        setCardData(availableFlats);
        setFilteredData(availableFlats);
        setDisplayedCards(availableFlats.slice(0, INITIAL_LOAD_COUNT));

        const minArea = Math.min(...availableFlats.map((flat: any) => parseFloat((flat.area || '').toString().replace(',', '.'))));
        const maxArea = Math.max(...availableFlats.map((flat: any) => parseFloat((flat.area || '').toString().replace(',', '.'))));
        setInitialAreaRange([minArea, maxArea]);
        setFilters(prevFilters => ({
          ...prevFilters,
          area_range: [minArea, maxArea],
        }));
        setSmallFilters((prevFilters) => ({
          ...prevFilters,
          price_range: [minPrice, maxPrice],
          floor_range: [
            Math.min(...availableFlats.map((flat: any) => flat.floor)),
            Math.max(...availableFlats.map((flat: any) => flat.floor)),
          ],
        }));

        setFeaturesData(featuresData);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [setIsInitialLoad]);

  const loadMoreCards = useCallback(() => {
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedCards(prevCards => {
        const nextCards = filteredData.slice(prevCards.length, prevCards.length + LOAD_MORE_COUNT);
        return [...prevCards, ...nextCards];
      });
      setIsLoadingMore(false);
    }, 500);
  }, [filteredData]);

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    const hasMoreCards = displayedCards.length < filteredData.length;

    const offset = isMobile ? 1400 : 300;

    if (scrollY + windowHeight >= documentHeight - offset && hasMoreCards && !isLoadingMore) {
      loadMoreCards();
    }
  }, [loadMoreCards, displayedCards.length, filteredData.length, isLoadingMore, isMobile]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const applySmallFilter = useCallback(
    (filters: FilterSmallState) => {
      setSmallFilters(filters);
      const filtered = cardData.filter((card) => {
        const discountedPrice = calculateDiscountedPrice(card.pricePerM2 ?? '0', card.discountProperty ?? 0, card.price ?? '0');
        const price = parseInt(discountedPrice.replace(/\s+/g, ''), 10);
        const floor = card.floor;

        const matchesType = filters.type.length === 0 || filters.type.includes(card.roomType);
        const matchesPrice = price >= filters.price_range[0] && price <= filters.price_range[1];
        const matchesFloor = floor >= filters.floor_range[0] && floor <= filters.floor_range[1];

        return matchesType && matchesPrice && matchesFloor;
      });

      setFilteredData(filtered);
      setDisplayedCards(filtered.slice(0, INITIAL_LOAD_COUNT));
    },
    [cardData]
  );

  const applyBigFilter = useCallback(
    (newFilters: FlatFilterBigState) => {
      setFilters(newFilters);
      setSmallFilters({
        type: newFilters.type,
        price_range: newFilters.price_range,
        floor_range: newFilters.floor_range,
      });

      const filtered = cardData.filter((card) => {
        const discountedPrice = calculateDiscountedPrice(card.pricePerM2 ?? '0', card.discountProperty ?? 0, card.price ?? '0');
        const price = parseInt(discountedPrice.replace(/\s+/g, ''), 10);
        const floor = card.floor;
        const area = parseFloat((card.area || '').toString().replace(',', '.'));

        const matchesType = newFilters.type.length === 0 || newFilters.type.includes(card.roomType);
        const matchesPrice = price >= newFilters.price_range[0] && price <= newFilters.price_range[1];
        const matchesFloor = floor >= newFilters.floor_range[0] && floor <= newFilters.floor_range[1];
        const matchesArea = newFilters.area_range
          ? area >= newFilters.area_range[0] - 0.05 && area <= newFilters.area_range[1] + 0.05
          : true;
        const matchesWindowOptions =
          newFilters.window_options.length === 0 ||
          newFilters.window_options.every((option) => card.windowOptions.some((wo: OptionData) => wo.name === option));
        const matchesSpecialOptions =
          newFilters.special_options.length === 0 ||
          newFilters.special_options.every((option) => card.specialOptions.some((so: OptionData) => so.name === option));

        return matchesType && matchesPrice && matchesFloor && matchesArea && matchesWindowOptions && matchesSpecialOptions;
      });

      setFilteredData(filtered);
      setDisplayedCards(filtered.slice(0, INITIAL_LOAD_COUNT));
      setFilters(newFilters);
    },
    [cardData]
  );

  const handleRemoveFilter = (filterType: string, value: string | number) => {
    if (filters) {
      const updatedFilters = { ...filters };
      if (filterType === 'window_options') {
        updatedFilters.window_options = updatedFilters.window_options.filter(option => option !== value);
      } else if (filterType === 'special_options') {
        updatedFilters.special_options = updatedFilters.special_options.filter(option => option !== value);
      } else if (filterType === 'area_range') {
        delete updatedFilters.area_range;
      }
      setFilters(updatedFilters);
      applyBigFilter(updatedFilters);
    }
  };

  const resetFilters = () => {
    setFilters({
      area_range: undefined,
      type: [],
      window_options: [],
      special_options: [],
      price_range: [0, 0],
      floor_range: [0, 0],
    });
    setSmallFilters({
      type: [],
      price_range: [0, 0],
      floor_range: [0, 0],
    });
    setFilteredData(cardData);
  };

  const openFiltersModal = () => {
    setIsFiltersModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFiltersModal = () => {
    setIsFiltersModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  const hasActiveFilters = filters && (
    filters.window_options.length > 0 ||
    filters.special_options.length > 0 ||
    (filters.area_range && (filters.area_range[0] !== initialAreaRange[0] || filters.area_range[1] !== initialAreaRange[1]))
  );

  return (
    <>
      <Helmet>
        <title>Квартиры | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/flats/status" />

        <meta property="og:title" content="Квартиры | Восток Строй" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/flats/status" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Квартиры | Восток Строй" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/status-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/status-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/status-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/status-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/status-fav-180x180.png" />
      </Helmet>

      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          {!isFiltersModalOpen && <Header theme={HeaderType.Light} />}
          <div className={styles.content}>
            <div className={styles.flat_wrapper}>
              <div className={styles.flat_content}>
                <div className={styles.page_title}>
                  <h1>Квартиры<br />в «Статусе»</h1>
                </div>
                <Features features={featuresData} hideIcons={true} />
                <Filter
                  onApplyFilter={applySmallFilter}
                  cardData={cardData}
                  onOpenFiltersModal={openFiltersModal}
                  smallFilters={smallFilters}
                  setSmallFilters={setSmallFilters}
                />
                {hasActiveFilters && (
                  <div className={styles.active_filters}>
                    {filters.window_options.map(option => (
                      <ActiveFilterButton
                        key={option}
                        label={option}
                        onClick={() => handleRemoveFilter('window_options', option)}
                        theme='light'
                      />
                    ))}
                    {filters.special_options.map(option => (
                      <ActiveFilterButton
                        key={option}
                        label={option}
                        onClick={() => handleRemoveFilter('special_options', option)}
                        theme='light'
                      />
                    ))}
                    {filters?.area_range && (filters.area_range[0] !== initialAreaRange[0] || filters.area_range[1] !== initialAreaRange[1]) && (
                      <ActiveFilterButton
                        label={`${filters.area_range[0].toLocaleString('ru-RU')}-${filters.area_range[1].toLocaleString('ru-RU')} м²`}
                        onClick={() => handleRemoveFilter('area_range', '')}
                        theme='light'
                      />
                    )}
                    {hasActiveFilters && (
                      <SelectButton onClick={resetFilters} theme='light'>
                        Сбросить фильтры
                      </SelectButton>
                    )}
                  </div>
                )}
                <div className={styles.list_wrapper}>
                  {filteredData.length === 0 ? (
                    <NotFoundBlock type="filter" />
                  ) : (
                    <div className={styles.flat_list}>
                      {displayedCards.map((data, index) => (
                        <div
                          key={data.id}
                          className={styles.flat_card}
                          style={{
                            animationDelay: `${index * (isMobile ? 0.02 : 0.03)}s`
                          }}
                        >
                          <Card data={data} isDiscounted={!!data.discountProperty} />
                        </div>
                      ))}
                      {isLoadingMore && (
                        <div className={styles.loader_container}>
                          <div className={styles.loader}>
                            <img src={CardsLoader} alt="Loading..." />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isFiltersModalOpen && (
            <FlatFilters
              onClose={closeFiltersModal}
              onApplyFilter={applyBigFilter}
              cardData={cardData}
              smallFilters={smallFilters}
              setSmallFilters={setSmallFilters}
            />
          )}
          <Footer theme={FooterType.Light} />
        </>
      )}
    </>
  );
}